/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
/* eslint-disable @atlaskit/design-system/use-tokens-shape */
/* eslint-disable @atlaskit/design-system/use-tokens-typography */
/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop */
/* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values */
/* eslint-disable @atlaskit/ui-styling-standard/no-unsafe-values */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	type MutableRefObject,
	type RefObject,
} from 'react';

import { cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import Lozenge from '@atlaskit/lozenge/new';

import { buildEdgesForAssets } from './edges';
import { getHeroLayout, MOBILE_ICON_SCALE, type HeroAsset } from './layout';
import { messages } from './messages';
import { useHeroMotion, type EdgeBinding } from './use-hero-motion';
import type { HeroViewportTier } from './use-viewport-tier';

const EDGE_COLOR = '#FFFFFF';
const EDGE_DASH = '1 3';

type LozengeAppearance = NonNullable<React.ComponentProps<typeof Lozenge>['appearance']>;

const LABEL_LOZENGE_SPECS: Record<string, { appearance: LozengeAppearance }> = {
	'status-done': { appearance: 'accent-green' },
	'status-on-track': { appearance: 'accent-blue' },
	'status-to-do': { appearance: 'accent-gray' },
	'status-at-risk': { appearance: 'accent-yellow' },
};

const styles = cssMap({
	root: {
		userSelect: 'none',
	},
	edgeSvg: {
		position: 'absolute',
		inset: 0,
		width: '100%',
		height: '100%',
		pointerEvents: 'none',
		overflow: 'visible',
	},
	icon: {
		position: 'absolute',
		height: 'auto',
		userSelect: 'none',
		willChange: 'transform, opacity',
		transform: 'translate(-50%, -50%)',
	},
	interactiveIcon: {
		cursor: 'grab',
		touchAction: 'none',
		'&:active': {
			cursor: 'grabbing',
		},
	},
	passiveIcon: {
		pointerEvents: 'none',
	},
	labelIcon: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		whiteSpace: 'nowrap',
	},
	avatarIcon: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: '50%',
		borderWidth: '2px',
		borderStyle: 'solid',
		borderColor: 'rgba(255, 255, 255, 0.72)',
		boxShadow: '0 8px 24px rgba(0, 0, 0, 0.24)',
		color: '#172B4D',
		fontFamily: "'Charlie Text', -apple-system, BlinkMacSystemFont, sans-serif",
		fontWeight: 600,
		fontSize: '14px',
	},
	shimmerGroup: {
		mixBlendMode: 'screen',
	},
});

export type BackgroundIconsProps = {
	stageRef: RefObject<HTMLDivElement | null>;
	introDone: boolean;
	onIntroComplete: () => void;
	replayHandleRef: MutableRefObject<(() => void) | null>;
	edges: {
		density: number;
		radius: number;
		opacity: number;
	};
	layout: {
		ovalSize: number;
		ovalAspectRatio: number;
		mobileOvalSize: number;
		mobileOvalAspectRatio: number;
	};
	ambient: {
		ambientAmp: number;
	};
	physics: {
		kSelf: number;
		kEdge: number;
		damping: number;
		flick: number;
	};
	intro: {
		avatarStaggerMs: number;
		edgeGrowMs: number;
		iconAppearMs: number;
		iconAppearOvershoot: number;
	};
	shimmer: {
		enabled: boolean;
		startOffsetMs: number;
		avgIntervalMs: number;
		maxConcurrent: number;
		durationMs: number;
		pulseLen: number;
		brightness: number;
		strokeWidth: number;
		glow: number;
	};
	viewportTier: HeroViewportTier;
	interactive: boolean;
};

export function BackgroundIcons({
	stageRef,
	introDone,
	onIntroComplete,
	replayHandleRef,
	edges: edgeParams,
	layout: layoutParams,
	ambient: ambientParams,
	physics: physicsParams,
	intro: introParams,
	shimmer: shimmerParams,
	viewportTier,
	interactive,
}: BackgroundIconsProps): JSX.Element {
	const { formatMessage } = useIntl();
	const iconElsRef = useRef<Map<string, HTMLElement>>(new Map());
	const edgeElsRef = useRef<Map<string, EdgeBinding>>(new Map());
	const shimmerSlotsRef = useRef<(SVGLineElement | null)[]>([]);
	const shimmerGradsRef = useRef<(SVGLinearGradientElement | null)[]>([]);

	const shimmerPoolSize = Math.max(1, Math.round(shimmerParams.maxConcurrent));
	const resolvedLayout = useMemo(
		() => getHeroLayout({ viewportTier, ...layoutParams }),
		[layoutParams, viewportTier],
	);
	const layoutAssets = resolvedLayout.assets;
	const referenceSize = useMemo(
		() => ({
			width: resolvedLayout.referenceWidth,
			height: resolvedLayout.referenceHeight,
		}),
		[resolvedLayout.referenceHeight, resolvedLayout.referenceWidth],
	);
	const assetById = useMemo(
		() => new Map(layoutAssets.map((asset) => [asset.id, asset] as const)),
		[layoutAssets],
	);
	const edges = useMemo(
		() =>
			buildEdgesForAssets(
				layoutAssets,
				edgeParams.density,
				edgeParams.radius,
				resolvedLayout.textSafeArea,
				referenceSize,
			),
		[
			edgeParams.density,
			edgeParams.radius,
			layoutAssets,
			referenceSize,
			resolvedLayout.textSafeArea,
		],
	);
	const handleIntroComplete = useCallback(() => onIntroComplete(), [onIntroComplete]);

	const { replayIntro } = useHeroMotion(
		{
			ambientAmp: ambientParams.ambientAmp,
			dynamics: {
				kSelf: physicsParams.kSelf,
				kEdge: physicsParams.kEdge,
				damping: physicsParams.damping,
				maxDt: 1 / 30,
				substeps: 2,
			},
			flick: physicsParams.flick,
			intro: introParams,
			edgeOpacity: edgeParams.opacity,
			shimmer: {
				...shimmerParams,
				maxConcurrent: shimmerPoolSize,
			},
			interactive,
			onIntroComplete: handleIntroComplete,
		},
		{
			stage: stageRef,
			icons: iconElsRef,
			edges: edgeElsRef,
			shimmerSlots: shimmerSlotsRef,
			shimmerGrads: shimmerGradsRef,
		},
		edges,
		layoutAssets,
		resolvedLayout.motionOrigin,
		referenceSize,
	);

	useEffect(() => {
		replayHandleRef.current = replayIntro;
	}, [replayHandleRef, replayIntro]);

	const labelMessages: Record<string, string> = {
		'status-done': formatMessage(messages.statusDone),
		'status-on-track': formatMessage(messages.statusOnTrack),
		'status-to-do': formatMessage(messages.statusToDo),
		'status-at-risk': formatMessage(messages.statusAtRisk),
	};

	return (
		<div aria-hidden="true" css={styles.root}>
			<svg
				css={styles.edgeSvg}
				viewBox={`0 0 ${resolvedLayout.referenceWidth} ${resolvedLayout.referenceHeight}`}
				preserveAspectRatio="none"
				aria-hidden="true"
			>
				<g>
					{edges.map((edge, i) => {
						const a = assetById.get(edge.from);
						const b = assetById.get(edge.to);
						if (!a || !b) return null;
						const x1 = a.x * resolvedLayout.referenceWidth;
						const y1 = a.y * resolvedLayout.referenceHeight;
						const x2 = b.x * resolvedLayout.referenceWidth;
						const y2 = b.y * resolvedLayout.referenceHeight;
						const edgeId = `${edge.from}__${edge.to}__${i}`;
						const edgeRenderKey = [
							edgeId,
							x1.toFixed(2),
							y1.toFixed(2),
							x2.toFixed(2),
							y2.toFixed(2),
						].join(':');
						return (
							<line
								key={edgeRenderKey}
								ref={(el) => {
									if (el) {
										edgeElsRef.current.set(edgeId, {
											el,
											edgeId,
											from: edge.from,
											to: edge.to,
											baseX1: x1,
											baseY1: y1,
											baseX2: x2,
											baseY2: y2,
											liveX1: x1,
											liveY1: y1,
											liveX2: x2,
											liveY2: y2,
										});
									} else {
										edgeElsRef.current.delete(edgeId);
									}
								}}
								x1={x1}
								y1={y1}
								x2={x2}
								y2={y2}
								stroke={EDGE_COLOR}
								strokeOpacity={introDone ? edgeParams.opacity : 0}
								strokeWidth={1}
								strokeDasharray={EDGE_DASH}
								vectorEffect="non-scaling-stroke"
							/>
						);
					})}
				</g>

				<defs>
					{Array.from({ length: shimmerPoolSize }).map((_, i) => (
						<linearGradient
							key={i}
							id={`hero-shimmer-${i}`}
							ref={(el) => {
								shimmerGradsRef.current[i] = el;
							}}
							gradientUnits="userSpaceOnUse"
						>
							<stop offset="0" stopColor={EDGE_COLOR} stopOpacity="0" />
							<stop offset="0.3" stopColor={EDGE_COLOR} stopOpacity="1" />
							<stop offset="0.7" stopColor={EDGE_COLOR} stopOpacity="1" />
							<stop offset="1" stopColor={EDGE_COLOR} stopOpacity="0" />
						</linearGradient>
					))}
				</defs>

				<g
					css={styles.shimmerGroup}
					style={{ filter: shimmerParams.glow > 0 ? `blur(${shimmerParams.glow}px)` : undefined }}
				>
					{Array.from({ length: shimmerPoolSize }).map((_, i) => (
						<line
							key={i}
							ref={(el) => {
								shimmerSlotsRef.current[i] = el;
							}}
							stroke={`url(#hero-shimmer-${i})`}
							strokeWidth={shimmerParams.strokeWidth}
							strokeLinecap="round"
							strokeOpacity={0}
							strokeDasharray={EDGE_DASH}
							vectorEffect="non-scaling-stroke"
						/>
					))}
				</g>
			</svg>

			{layoutAssets.map((asset) => (
				<HeroAssetNode
					key={asset.id}
					asset={asset}
					iconElsRef={iconElsRef}
					interactive={interactive}
					introDone={introDone}
					label={labelMessages[asset.id] ?? asset.alt ?? asset.id}
					referenceWidth={resolvedLayout.referenceWidth}
					viewportTier={viewportTier}
				/>
			))}
		</div>
	);
}

type HeroAssetNodeProps = {
	asset: HeroAsset;
	iconElsRef: MutableRefObject<Map<string, HTMLElement>>;
	interactive: boolean;
	introDone: boolean;
	label: string;
	referenceWidth: number;
	viewportTier: HeroViewportTier;
};

function HeroAssetNode({
	asset,
	iconElsRef,
	interactive,
	introDone,
	label,
	referenceWidth,
	viewportTier,
}: HeroAssetNodeProps): JSX.Element | null {
	const commonStyle = {
		left: `${asset.x * 100}%`,
		top: `${asset.y * 100}%`,
		opacity: introDone ? 1 : 0,
	} as const;
	const setIconRef = (el: HTMLElement | null) => {
		if (el) iconElsRef.current.set(asset.id, el);
		else iconElsRef.current.delete(asset.id);
	};

	if (asset.kind === 'label') {
		const spec = LABEL_LOZENGE_SPECS[asset.id] ?? { appearance: 'accent-gray' as const };
		const lozengeScale = viewportTier === 'mobile' ? MOBILE_ICON_SCALE : 1;
		return (
			<span
				id={asset.id}
				ref={setIconRef}
				data-asset-id={asset.id}
				data-asset-kind={asset.kind}
				css={[
					styles.icon,
					interactive ? styles.interactiveIcon : styles.passiveIcon,
					styles.labelIcon,
				]}
				style={{
					...commonStyle,
					minWidth: `${asset.width * referenceWidth}px`,
				}}
			>
				<span
					style={{
						transform: lozengeScale === 1 ? undefined : `scale(${lozengeScale})`,
						transformOrigin: 'center',
					}}
				>
					<Lozenge appearance={spec.appearance} spacing="spacious">
						{label}
					</Lozenge>
				</span>
			</span>
		);
	}

	if (asset.kind === 'avatar') {
		const avatarSize = asset.width * referenceWidth;
		if (asset.src) {
			return (
				<img
					id={asset.id}
					ref={setIconRef}
					data-asset-id={asset.id}
					data-asset-kind={asset.kind}
					src={asset.src}
					alt=""
					draggable={false}
					css={[
						styles.icon,
						interactive ? styles.interactiveIcon : styles.passiveIcon,
						styles.avatarIcon,
					]}
					style={{
						...commonStyle,
						width: `${avatarSize}px`,
						height: `${avatarSize}px`,
						objectFit: 'cover',
					}}
				/>
			);
		}

		return (
			<span
				id={asset.id}
				ref={setIconRef}
				data-asset-id={asset.id}
				data-asset-kind={asset.kind}
				css={[
					styles.icon,
					interactive ? styles.interactiveIcon : styles.passiveIcon,
					styles.avatarIcon,
				]}
				style={{
					...commonStyle,
					width: `${avatarSize}px`,
					height: `${avatarSize}px`,
					backgroundColor: getAvatarColor(asset.id),
				}}
			>
				{getAvatarInitials(asset.id)}
			</span>
		);
	}

	if (!asset.src) return null;
	return (
		<img
			id={asset.id}
			ref={setIconRef}
			data-asset-id={asset.id}
			data-asset-kind={asset.kind}
			src={asset.src}
			alt=""
			draggable={false}
			css={[styles.icon, interactive ? styles.interactiveIcon : styles.passiveIcon]}
			style={{
				...commonStyle,
				width: `${asset.width * 100}%`,
			}}
		/>
	);
}

const AVATAR_COLORS = ['#B3DF72', '#9F8FEF', '#7AB2FF', '#F5CD47', '#FFA364', '#6CC3E0'];

function getAvatarColor(id: string): string {
	let sum = 0;
	for (let i = 0; i < id.length; i++) sum += id.charCodeAt(i);
	return AVATAR_COLORS[sum % AVATAR_COLORS.length];
}

function getAvatarInitials(id: string): string {
	const name = id.replace(/^avatar-/, '');
	return name
		.split('-')
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0]?.toUpperCase() ?? '')
		.join('');
}
